import { useState, useRef, useEffect } from 'react'
import { ShieldCheck, Loader2 } from 'lucide-react'
import { verifyTotp } from '@/api/config'
import { ApiError } from '@/lib/api-error'

interface TotpVerifyModalProps {
  onVerified: (opToken: string) => void
  onCancel: () => void
  actionLabel?: string
}

export function TotpVerifyModal({ onVerified, onCancel, actionLabel = 'Continuar' }: TotpVerifyModalProps) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (code.length !== 6 || loading) return
    setLoading(true)
    setError(null)
    try {
      const { op_token } = await verifyTotp(code)
      onVerified(op_token)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Error al verificar')
      setCode('')
      inputRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm bg-surface border border-border rounded-xl shadow-xl p-6 flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-accent shrink-0" />
          <h2 className="text-text-primary font-semibold text-base">Verificación de seguridad</h2>
        </div>

        <p className="text-text-muted text-xs">
          Ingresa el código de 6 dígitos de tu aplicación autenticadora para continuar.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError(null) }}
            placeholder="000000"
            className={`w-full bg-background border rounded-lg px-4 py-2.5 text-center text-xl font-mono text-text-primary tracking-widest focus:outline-none focus:ring-1 focus:ring-accent transition-colors ${error ? 'border-red-500' : 'border-border'}`}
          />

          {error && (
            <p className="text-red-500 text-xs text-center">{error}</p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="flex-1 px-3 py-2 border border-border text-text-primary text-xs font-medium rounded-lg hover:bg-background transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={code.length !== 6 || loading}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-accent text-background text-xs font-semibold rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
            >
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {actionLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
